/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService, BaseServiceRegion } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import { ComponentServiceConfiguration } from './Component.service.config';
import {
  ComponentServiceComponentSetDto,
  ComponentServiceCreateComponentSetDto,
  ComponentServiceDeleteComponentDto,
  ComponentServiceGetComponentDto,
  ComponentServiceGetComponentManifestDto,
  ComponentServiceGetComponentPreviewDto,
  ComponentServicePatchComponentSetDetailsDto,
} from './Component.service.dto';
import { EntityComponents } from './Entities/Components/Components.entity';
import {
  EntityComponentManifest,
  EntityComponentSet,
  EntityComponentSetItem,
  EntityComponentVersion,
  EntityComponentVersions,
} from './Entities/entities';

/**
 * The component service.
 * @export
 * @class ComponentService
 */
export class ComponentService extends BaseService {
  /**
   * Creates an instance of ComponentService.
   * @param {ComponentServiceConfiguration} configuration The configuration options to accept.
   * @memberof ComponentService
   */
  public constructor(configuration: ComponentServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates url to use for component service management api requests.
   * @protected
   * @returns {string | null} The tenant specific api url to use. Returns null if tenant is undefined.
   * @memberof ComponentService
   */
  protected getManagementUrl(): string | null {
    // Note: Region is set as `au` for now
    return this.getServiceUrlByRegion(`components-management`, BaseServiceRegion.au);
  }

  /**
   * Creates url to use for component service render api requests.
   * @protected
   * @returns {string | null} The tenant specific api url to use. Returns null if tenant is undefined.
   * @memberof ComponentService
   */
  protected getRenderUrl(type: 'edge' | 'server' = 'server'): string | null {
    // Note: Region is set as `au` for now
    return type === 'edge'
      ? this.getServiceUrlByRegion(`components-edge`, BaseServiceRegion.au)
      : this.getServiceUrlByRegion(`components-render`, BaseServiceRegion.au);
  }

  /**
   * Creates api url for component previews.
   * @param {ComponentServiceGetComponentPreviewDto} dto The details of the target component.
   * @returns {string} The api url for a component's preview.
   * @memberof ComponentService
   */
  createComponentPreviewUrl(dto: ComponentServiceGetComponentPreviewDto): string {
    const { name, namespace, previewKey, version, environmentVars, type } = dto;

    const envVars = environmentVars
      ? '&' +
        Object.entries(environmentVars)
          .map(([key, value]) => `_previewEnvVars[${key}]=${encodeURIComponent(value)}`)
          .join('&')
      : '';

    const componentIdentifier =
      type === 'server' ? `${namespace}/${name}/${version}` : `${namespace}__${name}__${version}`;

    return `${this.getRenderUrl(type)}/r/${componentIdentifier}?_previewKey=${previewKey}${envVars}`;
  }

  /**
   * Creates api url for component manifest.
   * @param {ComponentServiceGetComponentManifestDto} dto The details of the target component.
   * @returns {string} The api url for a component's manifest.
   * @memberof ComponentService
   */
  createComponentManifestUrl(dto: ComponentServiceGetComponentManifestDto): string {
    const { name, namespace, version } = dto;

    return `${this.getRenderUrl()}/d/${namespace}/${name}/${version}/manifest.json`;
  }

  /**
   * Attempt to get the component manifest.
   * @param {ComponentServiceGetComponentManifestDto} dto The details of the target component.
   * @returns {Observable<EntityComponentManifest>} The observable component.
   * @memberof ComponentService
   */
  getComponentManifest(dto: ComponentServiceGetComponentManifestDto): Observable<EntityComponentManifest> {
    return this.httpService
      .get<unknown, HttpResponse>(this.createComponentManifestUrl(dto), {
        headers: this.getHeaders(),
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentManifest> => {
          return this._handleResponseError<EntityComponentManifest>(value);
        }),
      );
  }

  /**
   * Creates api url for component set.
   * @param {ComponentServiceComponentSetDto} dto Optional dto parameter if getting component set details.
   * @returns {string} The api url for component sets. Returns null if the base url is null.
   * @memberof ComponentService
   */
  createComponentSetUrl(dto: ComponentServiceComponentSetDto): string | null {
    const { page, sortBy, webpath } = dto;
    const managementUrl = this.getManagementUrl();

    if (!managementUrl) {
      return null;
    }

    let componentSetUrl = `${managementUrl}/v1/component-set`;

    if (webpath) {
      componentSetUrl += `/${webpath}`;
    }

    if (page) {
      componentSetUrl += `?page=${page}`;
    }

    if (sortBy) {
      componentSetUrl += `&sort[]=${sortBy}`;
    }

    return componentSetUrl;
  }

  /**
   * Attempt to get component sets.
   * @param {ComponentServiceComponentSetDto} dto The details for the component sets to get.
   * @returns {Observable<EntityComponentSet | null>} The observable component set.
   * @memberof ComponentService
   */
  getComponentSet(dto: ComponentServiceComponentSetDto): Observable<EntityComponentSet | null> {
    const requestUrl = this.createComponentSetUrl(dto);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentSet> => {
          return this._handleResponseError<EntityComponentSet>(value);
        }),
      );
  }

  /**
   * Attempt to create a component set.
   * @param {ComponentServiceCreateComponentSetDto} dto The details of the component set to create.
   * @returns {Observable<EntityComponentSetItem | null>} The observable component set.
   * @memberof ComponentService
   */
  createComponentSet(dto: ComponentServiceCreateComponentSetDto): Observable<EntityComponentSetItem | null> {
    const { name, webpath, description } = dto;
    const requestUrl = this.createComponentSetUrl({});

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        body: {
          description,
          displayName: name,
          webPath: webpath,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentSetItem> => {
          return this._handleResponseError<EntityComponentSetItem>(value);
        }),
      );
  }

  /**
   * Attempt to get component set details.
   * @param {ComponentServiceComponentSetDto} dto The details of the component set to get.
   * @returns {Observable<EntityComponentSetItem | null>} The observable component set.
   * @memberof ComponentService
   */
  getComponentSetDetails(dto: ComponentServiceComponentSetDto): Observable<EntityComponentSetItem | null> {
    const requestUrl = this.createComponentSetUrl(dto);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentSetItem> => {
          return this._handleResponseError<EntityComponentSetItem>(value);
        }),
      );
  }

  /**
   * Attempt to patch component set details.
   * @param {ComponentServicePatchComponentSetDetailsDto} dto The details of the component set to get.
   * @returns {Observable<EntityComponentSetItem | null>} The observable component set.
   * @memberof ComponentService
   */
  patchComponentSetDetails(
    dto: ComponentServicePatchComponentSetDetailsDto,
  ): Observable<EntityComponentSetItem | null> {
    const { webpath, ...rest } = dto;
    const requestUrl = this.createComponentSetUrl({ webpath });

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        body: rest,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentSetItem> => {
          return this._handleResponseError<EntityComponentSetItem>(value);
        }),
      );
  }

  /**
   * Creates api url for components.
   * @param {ComponentServiceGetComponentDto} dto Optional dto parameter if getting component set details.
   * @returns {string | null} The api url for component sets. Returns null if tenant is undefined.
   * @memberof ComponentService
   */
  createComponentUrl(dto: ComponentServiceGetComponentDto): string | null {
    const { page, webpath, version, search } = dto;
    const managementUrl = this.getManagementUrl();

    if (!managementUrl) {
      return null;
    }

    let componentUrl = `${managementUrl}/v1/component`;

    if (webpath) {
      componentUrl += `/${webpath}`;
    }

    if (version) {
      componentUrl += `/${version}`;
    }

    if (page) {
      componentUrl += `?page=${page}`;
    }

    if (search) {
      componentUrl += `&searchComponentName=${search}`;
    }

    return componentUrl;
  }

  /**
   * Creates api url for deleting components.
   * @param {ComponentServiceDeleteComponentDto} dto Optional dto parameter if deleting component.
   * @returns {string | null} The api url for deleting components. Returns null if tenant is undefined.
   * @memberof ComponentService
   */
  createDeleteComponentUrl(dto: ComponentServiceDeleteComponentDto): string | null {
    const { deleteSchemas, webpath } = dto;
    const managementUrl = this.getManagementUrl();

    if (!managementUrl) {
      return null;
    }

    let deleteComponentUrl = `${managementUrl}/v1/component`;

    if (webpath) {
      deleteComponentUrl += `/${webpath}`;
    }

    if (deleteSchemas) {
      deleteComponentUrl += `?deleteSchemas=${deleteSchemas}`;
    }

    return deleteComponentUrl;
  }

  /**
   * Attempt to get components.
   * @param {ComponentServiceGetComponentDto} dto The details for the components list to get.
   * @returns {Observable<EntityComponents | null>} The observable components list.
   * @memberof ComponentService
   */
  getComponent(dto: ComponentServiceGetComponentDto): Observable<EntityComponents | null> {
    const requestUrl = this.createComponentUrl(dto);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponents> => {
          return this._handleResponseError<EntityComponents>(value);
        }),
      );
  }

  /**
   * Attempt to delete component.
   * @param {ComponentServiceDeleteComponentDto} dto The details for the component to delete.
   * @returns {Observable<{} | null>} The observable response.
   * @memberof ComponentService
   */
  deleteComponent(dto: ComponentServiceDeleteComponentDto): Observable<{} | null> {
    const requestUrl = this.createDeleteComponentUrl(dto);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error('Undefined tenant');
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<{}> => {
          return this._handleResponseError<{}>(value);
        }),
      );
  }

  /**
   * Attempt to get components versions.
   * @param {ComponentServiceGetComponentDto} dto The details for the components list to get.
   * @returns {Observable<EntityComponentVersions | null>} The observable component versions.
   * @memberof ComponentService
   */
  getComponentVersions(dto: ComponentServiceGetComponentDto): Observable<EntityComponentVersions | null> {
    const requestUrl = this.createComponentUrl(dto);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentVersions> => {
          return this._handleResponseError<EntityComponentVersions>(value);
        }),
      );
  }

  /**
   * Attempt to get component version.
   * @param {ComponentServiceGetComponentDto} dto The details for the component version to get.
   * @returns {Observable<EntityComponentVersion | null>} The observable component version.
   * @memberof ComponentService
   */
  getComponentVersion(dto: ComponentServiceGetComponentDto): Observable<EntityComponentVersion | null> {
    const requestUrl = this.createComponentUrl(dto);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityComponentVersion> => {
          return this._handleResponseError<EntityComponentVersion>(value);
        }),
      );
  }
}
